import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../services/api';
import AdminShell from './AdminShell';

const crumbs = [
  { label: 'Dashboard', href: '/admin' },
  { label: 'Agents' },
  { label: 'Logs', href: '/admin/logs'  },
];

export default function AdminAgentListPage() {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [agents,  setAgents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null); // agent_id being toggled

  useEffect(() => {
    if (user && !user.isSuperAdmin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/admin/agents')
      .then(data => setAgents(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleToggle(e, agentId) {
    e.stopPropagation();
    setToggling(agentId);
    try {
      const updated = await apiFetch(
        `/api/admin/agents/${agentId}/toggle-active`,
        { method: 'PATCH' }
      );
      setAgents(prev =>
        prev.map(a => a.agent_id === agentId ? { ...a, is_active: updated.is_active } : a)
      );
    } catch {}
    finally { setToggling(null); }
  }

  return (
    <AdminShell crumbs={crumbs}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px',
            fontWeight: '400', marginBottom: '4px' }}>
            Agents
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
            {agents.length} agent{agents.length !== 1 ? 's' : ''} registered
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/agents/new')}
          style={primaryBtn}>
          + New Agent
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ color: 'var(--ink-mute)', fontSize: '13px' }}>Loading…</p>
      ) : agents.length === 0 ? (
        <div style={{
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: 'var(--r)', padding: '48px', textAlign: 'center',
        }}>
          <p style={{ color: 'var(--ink-mute)', fontSize: '14px' }}>
            No agents yet.{' '}
            <button onClick={() => navigate('/admin/agents/new')}
              style={{ background: 'none', border: 'none', color: 'var(--blue)',
                cursor: 'pointer', fontSize: '14px', padding: 0 }}>
              Create one →
            </button>
          </p>
        </div>
      ) : (
        <div style={{
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          borderRadius: 'var(--r)', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--paper-3)' }}>
                {['Name', 'Key', 'Provider', 'Model','Output Tokens',  'Status', 'Updated', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left',
                    fontSize: '10px', fontWeight: '600', letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--ink-mute)',
                    background: 'var(--paper-2)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, i) => (
                <tr
                  key={agent.agent_id}
                  onClick={() => navigate(`/admin/agents/${agent.agent_id}`)}
                  style={{
                    borderBottom: i < agents.length - 1
                      ? '1px solid var(--paper-3)' : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--paper-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={td}>
                    <div style={{ fontWeight: '500', fontSize: '13px' }}>
                      {agent.name}
                    </div>
                    {agent.short_description && (
                      <div style={{ fontSize: '11px', color: 'var(--ink-mute)',
                        marginTop: '2px', maxWidth: '260px',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' }}>
                        {agent.short_description}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px',
                      color: 'var(--ink-soft)' }}>
                      {agent.agent_key}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: '12px', color: 'var(--ink-soft)' }}>
                      {agent.llm_provider_code ?? '—'}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: '12px', color: 'var(--ink-soft)',
                      fontFamily: 'monospace' }}>
                      {agent.llm_model_code ?? '—'}
                    </span>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: '12px', color: 'var(--ink-soft)',
                      fontFamily: 'monospace' }}>
                      {agent.recommended_output_token ?? '—'}
                    </span>
                  </td>

                  <td style={td}>
                    <button
                      onClick={e => handleToggle(e, agent.agent_id)}
                      disabled={toggling === agent.agent_id}
                      style={{
                        padding: '3px 10px', borderRadius: '100px',
                        border: 'none', cursor: 'pointer', fontSize: '11px',
                        fontWeight: '600', fontFamily: 'var(--sans)',
                        opacity: toggling === agent.agent_id ? 0.5 : 1,
                        background: agent.is_active
                          ? 'rgba(45,138,110,0.12)' : 'rgba(180,180,180,0.15)',
                        color: agent.is_active ? '#2d8a6e' : 'var(--ink-mute)',
                        transition: 'all 0.15s',
                      }}>
                      {agent.is_active ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                      {new Date(agent.updated_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span style={{ fontSize: '12px', color: 'var(--ink-mute)' }}>→</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}

const td = {
  padding: '12px 16px',
  fontSize: '13px',
  verticalAlign: 'middle',
};

const primaryBtn = {
  padding: '9px 18px', background: 'var(--accent)', color: '#fff',
  border: 'none', borderRadius: '6px', fontFamily: 'var(--sans)',
  fontSize: '13px', fontWeight: '600', cursor: 'pointer',
};
